import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// API
import {
  postCreateUser,
  postUserHubAssociation,
} from "@intelligentlilli/api-layer";
// State
import { useDispatch, useSelector } from "react-redux";
import {
  setLoading,
  setRefreshStaff,
  setRefreshOrganisationHubs,
} from "../../State/slices/session-slice";
// Constants
import { getNewUserRoleDescription } from "../Constants";

export type ServiceUser = {
  id: number;
  label: string;
  value: string;
};

type UseAddNewStaff = {
  onTrack: (eventName: string, eventProperties: Record<string, string>) => void;
  hasCreatedFamily: boolean;
};

export const useAddNewStaff = ({
  onTrack,
  hasCreatedFamily,
}: UseAddNewStaff) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const server = useSelector((state) => state.session.server);
  const serviceUsers = useSelector((state) => state.serviceUsers);
  const serviceUsersToSelect = serviceUsers?.map((su) => {
    return {
      id: su?.id, // number
      label: su?.userstring,
      value: su?.userstring,
    };
  });

  const [newUserRoles, setNewUserRoles] = useState<
    string | { key: string; value: string }
  >("");
  const [createStaffEmailError, setCreateStaffError] = useState("");
  const [staffCreated, setStaffCreated] = useState<
    undefined | { id: string; name: string; email: string }
  >(undefined);
  const [serviceUsersAssigned, setServiceUsersAssigned] = useState<
    undefined | ServiceUser[]
  >(undefined);

  const [isProcessingForm, setIsProcessingForm] = useState(false);
  const [showSplashScreen, setShowSplashScreen] = useState(false);
  const [formSuccess, setFormSuccess] = useState(false);
  const [addingUser, setAddingUser] = useState(false);

  const newUserDescription = getNewUserRoleDescription[newUserRoles?.key];

  // Form helpers
  const resetFormState = () => {
    setIsProcessingForm(false);
    setCreateStaffError("");
    setShowSplashScreen(false);
    setServiceUsersAssigned(undefined);
    setStaffCreated(undefined);
    setFormSuccess(false);
    // The Invite Family form supports a UI where you can, without exiting the form,
    // invite another family member. This UI is not supported when creating staff.
    hasCreatedFamily &&
      onTrack("Invite Family", {
        action: "form_opened",
      });
  };

  // Assign residents to newly created staff
  // If new staff member was created successfully & they have been assigned residents
  useEffect(() => {
    // The 'assignUsersToStaff' function must be inside the useEffect Hook, otherwise it will trigger a re-render.
    // Another option is to move it inside the useEffect callback.
    async function assignUsersToStaff(
      staffId: string,
      susToAssociate: ServiceUser[]
    ) {
      const promises = [];
      for (let i = 0; i < susToAssociate.length; i++) {
        promises.push(
          postUserHubAssociation(
            server,
            staffId,
            susToAssociate?.[i]?.id,
            "web"
          )
        );
      }
      await Promise.all(promises)
        .then(() => {
          // update local state
          setShowSplashScreen(true);
          setFormSuccess(true);
          setServiceUsersAssigned(susToAssociate);
          setIsProcessingForm(false);
          // refresh redux
          dispatch(setRefreshStaff(true));
          dispatch(setRefreshOrganisationHubs(true));
          dispatch(setLoading(false));
        })
        .catch((err) => {
          console.log("postUserHubAssociation err:", err);
          setShowSplashScreen(true);
          setServiceUsersAssigned(undefined);
          setFormSuccess(false);
          setIsProcessingForm(false);
        });
    }

    if (staffCreated?.id && serviceUsersAssigned) {
      assignUsersToStaff(staffCreated.id, serviceUsersAssigned);
    }
  }, [staffCreated?.id, dispatch, navigate, server, serviceUsersAssigned]);

  async function createStaff(
    forename: string,
    surname: string,
    email: string,
    susToAssociate: ServiceUser[]
  ) {
    const roles = newUserRoles?.value; // taken from local state, changes with the button

    postCreateUser(server, forename, surname, email, roles, "web")
      .then((res) => {
        if (!res.ok) {
          console.log("postCreateUser err:", res?.data);
          setCreateStaffError("Sorry, we could not create this staff member");
          dispatch(setLoading(false));
          setIsProcessingForm(false);
        } else {
          // on success, store response data locally
          setStaffCreated({
            name: `${forename} ${surname}`,
            ...res?.data,
          });
          // refresh redux
          dispatch(setRefreshStaff(true));
          dispatch(setRefreshOrganisationHubs(true));
          dispatch(setLoading(false));
          // Amplitude tracking
          onTrack(hasCreatedFamily ? "Invite Family" : "Staff Creation", {
            action: "form_submitted",
            staff_type: newUserRoles?.key.toLowerCase(),
          });
          // If staff was assigned residents then update local state
          // Otherwise, skip assign SU stage and show success splash right away
          if (susToAssociate) {
            setServiceUsersAssigned(susToAssociate);
          } else {
            setShowSplashScreen(true);
            setServiceUsersAssigned(undefined);
            setFormSuccess(true);
            setIsProcessingForm(false);
          }
          return res?.data;
        }
      })
      .catch((err) => {
        console.log("postCreateUser err:", err);
        setCreateStaffError("Couldn't create staff at this time");
        // On error, show the modal, set the error, and store the name of the staff
        dispatch(setLoading(false));
        setShowSplashScreen(true);
        setFormSuccess(false);
        setStaffCreated(undefined);
        setIsProcessingForm(false);
        return err;
      });
  }

  // Handle the manager adding a new staff member to their organisation
  const onAddStaffMember = async (
    forename: string,
    surname: string,
    email: string,
    susToAssociate: ServiceUser[]
  ) => {
    setCreateStaffError("");
    dispatch(setLoading(true));
    setIsProcessingForm(true);
    const staffResponse = await createStaff(
      forename,
      surname,
      email,
      susToAssociate
    );
    // If staffReponse is successful, it will update local state, triggering the useEffect hook.
    // And if residents were assigned to the new staff, it triggers the "assignUsersToStaff" async function
    return staffResponse;
  };

  return {
    newUserDescription,
    newUserRoles,
    onAddStaffMember,
    resetFormState,
    setNewUserRoles,
    serviceUsersAssigned,
    showSplashScreen,
    isProcessingForm,
    formSuccess,
    setFormSuccess,
    staffCreated,
    createStaffEmailError,
    setCreateStaffError,
    addingUser,
    setAddingUser,
    serviceUsersToSelect,
  };
};
